<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Equipment Roles' | term }}</h1>
                <v-btn small color="secondary" class="ml-3" @click="create()">
                    <v-icon left>mdi-plus</v-icon>{{ $t('shared.button.add') }}
                </v-btn>
                <v-menu
                    v-model="importDialog"
                    max-width="310px"
                    :close-on-content-click="false"
                    v-if="!!data && !data.length"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" small class="ml-1" color="secondary">
                            <v-icon left>mdi-upload</v-icon>{{ $t('shared.button.import') }}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-text>
                            <input ref="inputUpload" type="file" accept="text/csv" @change="getFile($event)" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" :disabled="file === null" @click="importData">{{ $t('shared.button.upload') }}</v-btn>
                            <v-btn text @click="importDialog = false">{{ $t('shared.button.cancel') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn small class="ml-1" color="gray" v-if="!!data && data.length" @click="exportData()">
                    <v-icon left>mdi-download</v-icon>{{ $t('shared.button.export') }}
                </v-btn>
                <v-btn small color="primary" class="ml-3" v-if="dirty" @click="saveList()">
                    <v-icon left>mdi-check</v-icon>{{ $t('shared.button.save') }}
                </v-btn>
                <v-btn small color="grey" class="ml-3" v-if="dirty" @click="loadData()">
                    <v-icon left>mdi-close</v-icon>{{ $t('shared.button.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <admin-sandpit-visibility-selector :disabled="!canToggleSandpitVisibility"></admin-sandpit-visibility-selector>
            </v-card-title>
            <standard-wrapping-reorderable
                :items="sortedAndFilteredActive"
                :item-id="'id'"
                :item-order="'displayOrder'"
                @reordered="onItemsReordered"
                item-class="equip-role-item"
                handle-class="equip-role-handle"
            >
                <v-data-table disable-pagination hide-default-footer :headers="headers" :items="sortedAndFilteredActive">
                    <template v-slot:item="{ item }">
                        <tr :class="{ dirty: item.dirty, 'equip-role-item': true }">
                            <td>
                                <div :hidden="!canReorder" class="no-wrap">
                                    <v-icon class="equip-role-handle">mdi-drag</v-icon>
                                </div>
                            </td>
                            <td>
                                <v-tooltip v-if="item.isCrewType" right>
                                    <template v-slot:activator='{ on }'>
                                        <v-icon  color="primary" v-on="on">mdi-account-group-outline</v-icon>
                                    </template>
                                    <span>{{ $t('equipment-roles.supports-special-display-options') }}</span>
                                </v-tooltip>
                            </td>
                            <td><admin-sandpit-item-name :is-sandpit="item.isSandpitUseOnly">{{ item.name }}</admin-sandpit-item-name></td>
                            <td>
                                <div class="no-wrap">
                                    {{ item.displayOrder }}
                                    <v-btn text icon @click="edit(item)">
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </standard-wrapping-reorderable>
            <v-card-title v-if="sortedAndFilteredArchived.length > 0">
                <h2 class='subtitle-1'><v-icon color="primary" left>mdi-archive-outline</v-icon>{{ $t('equipment-roles.archived-term', { term: $termSync('Equipment Roles') }) }}</h2>
            </v-card-title>
            <v-data-table
                disable-pagination
                hide-default-footer
                :headers="headers"
                :items="sortedAndFilteredArchived"
                v-if="sortedAndFilteredArchived.length > 0"
            >
                <template v-slot:item="{ item }">
                    <tr :class="{ dirty: item.dirty }">
                        <td>
                        </td>
                        <td>
                            <v-tooltip v-if="item.isCrewType" right>
                                <template v-slot:activator='{ on }'>
                                    <v-icon  color="primary" v-on="on">mdi-account-outline</v-icon>
                                </template>
                                <span>{{ $t('equipment-roles.supports-special-display-options') }}</span>
                            </v-tooltip>
                            <v-tooltip v-else right>
                                <template v-slot:activator='{ on }'>
                                    <v-icon  color="primary" v-on="on">mdi-truck-outline</v-icon>
                                </template>
                                <span>{{ $t('equipment-roles.standard-term', { term: $termSync('Equipment Role') }) }}</span>
                            </v-tooltip>
                        </td>
                        <td><admin-sandpit-item-name :is-sandpit="item.isSandpitUseOnly">{{ item.name }}</admin-sandpit-item-name></td>
                        <td>
                            <div class="no-wrap">
                                {{ item.displayOrder }}
                                <v-btn text icon @click="edit(item)">
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </div>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
        <standard-dialog :render="!!selected" :value='showModal' :max-width="900" persistent>
            <template v-slot:title>
                <span v-if="selected.id">{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class="ml-1">{{ 'Equipment Role' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="form" :lazy-validation="true">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="10" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('shared.label.name')"
                                    :rules="[$rules.required(), $rules.maxLength(50)]"
                                    v-model="selected.name"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-checkbox
                                    :label="$t('equipment-roles.crew-type')"
                                    v-model="selected.isCrewType"
                                    hide-details></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
                <v-alert
                    v-if='editingSelected && !selected.canDelete && !selected.loadingCanDelete && !selected.isArchived'
                    color='warning'
                    class="white--text">
                    This {{ 'Equipment Role' | term }} is in use and cannot be deleted.
                </v-alert>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='editingSelected && !selected.isArchived' @click='del' :disabled='!selected.canDelete' :loading='selected.loadingCanDelete'>{{ $t('shared.button.delete') }}</v-btn>
                <v-btn color="red" text v-if="editingSelected && !selected.isArchived" @click="archiveEquipmentRole"><v-icon left>mdi-archive-outline</v-icon>{{ $t('shared.button.archive') }}</v-btn>
                <v-btn color="red" text v-if="editingSelected && selected.isArchived" @click="restoreEquipmentRole"><v-icon left>mdi-archive-arrow-up-outline</v-icon>{{ $t('shared.button.restore') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save()' v-if="!selected.isArchived">{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import EquipmentRoles from '@/lib/data/EquipmentRoles';
import DownloadHelper from '@/lib/DownloadHelper';
import { mapState } from 'pinia';
import { AdminSandpitVisibility, useSandpitStore } from '@/lib/stores/SandpitStore';
import AdminSandpitVisibilitySelector from '@/components/Admin/Sandpit/AdminSandpitVisibilitySelector.vue';
import AdminSandpitItemName from '@/components/Admin/Sandpit/AdminSandpitItemName.vue';
import StandardWrappingReorderable from '@/components/Shared/Common/StandardWrappingReorderable.vue';

export default {
    components: { StandardWrappingReorderable, AdminSandpitVisibilitySelector, AdminSandpitItemName },
    data() {
        return {
            data: null,
            showModal: false,
            selected: null,
            editingSelected: false,
            importDialog: false,
            file: null,
            reordered: false,
            headers: [
                { text: '', value: 'handle', width: '24px' },
                { text: '', value: 'roleType', width: '24px' },
                { text: 'Name', value: 'name', width: '80%' },
                { text: '', value: 'id', align: 'right' }
            ]
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            var data = await EquipmentRoles.get();
            this.reordered = false;

            for (let d of data) {
                d.dirty = false;
            }

            this.data = data;
        },
        create() {
            var displayOrder = this.data.length ? this.data[this.data.length - 1].displayOrder + 1 : 1;
            this.edit({
                name: '',
                displayOrder: displayOrder,
                isCrewType: false,
            }, true);
        },
        edit(item, creating = false) {
            this.selected = {
                ...item,
                loadingCanDelete: !creating
            };

            if(!creating){
                EquipmentRoles.canDelete(this.selected.id).then(response => {
                    if(this.selected?.id === item.id)
                        this.selected.canDelete = response.canDelete;
                }).finally(() => {
                    if(this.selected?.id === item.id)
                        this.selected.loadingCanDelete = false;
                });
            }

            this.editingSelected = !creating;

            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        onItemsReordered(reorderedItems) {
            // Update display orders based on new order
            reorderedItems.forEach((item, index) => {
                const originalItem = this.data.find(x => x.id === item.id);
                if (originalItem && originalItem.displayOrder !== index + 1) {
                    originalItem.displayOrder = index + 1;
                    originalItem.dirty = true;
                    this.reordered = true;
                }
            });
        },
        async del() {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Equipment Role'),  { })) {
                await EquipmentRoles.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Equipment Role')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        async archiveEquipmentRole() {
            if(this.selected == null)
                return;

            this.$wait.start('saving');
            try {
                await EquipmentRoles.archiveEquipmentRole(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Equipment Role')} Archived`);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async restoreEquipmentRole() {
            if(this.selected == null)
                return;

            this.$wait.start('saving');
            try {
                await EquipmentRoles.restoreEquipmentRoleFromArchive(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Equipment Role')} Restored`);
                await this.loadData();
            } finally {
                this.showModal = false;
                this.$wait.end('saving');
            }
        },
        async saveList() {
            for (let x of this.data) {
                if (!x.dirty) {
                    continue;
                }
                await EquipmentRoles.save(x);
            }
            EventBus.$emit(Events.ToastSuccess, `${this.$pluralSync('Equipment Roles')} Saved`);
            await this.loadData();
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }
            await EquipmentRoles.save(this.selected);
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Equipment Role')} Saved`);
            this.showModal = false;
            await this.loadData();
        },
        async exportData() {
            const result = await EquipmentRoles.export();
            var csv = result.csvFile;
            var blob = DownloadHelper.makeBlobFromFileString(csv);
            DownloadHelper.download('EquipmentRoles.csv', blob);
        },
        async importData() {
            try {
                this.$wait.start('saving');
                this.uploadResult = null;
                await EquipmentRoles.import(this.file);
                this.importDialog = false;
                await this.loadData();
            } finally {
                this.$wait.end('saving');
            }
        },
        async getFile(e) {
            this.file = e.target.files[0];
        }
    },
    computed: {
        ...mapState(useSandpitStore, ['adminSandpitVisibility']),
        dirty() {
            for (let x of this.data) {
                if (x.dirty) {
                    return true;
                }
            }
            return false;
        },
        canReorder() {
            return this.adminSandpitVisibility === AdminSandpitVisibility.BOTH;
        },
        canToggleSandpitVisibility() {
            return !this.reordered;
        },
        sandpitFilter() {
            if(this.adminSandpitVisibility === AdminSandpitVisibility.BOTH)
                return x=>true;
            else if(this.adminSandpitVisibility === AdminSandpitVisibility.ONLY_SANDPIT)
                return x=>x.isSandpitUseOnly;
            else
                return x=>!x.isSandpitUseOnly;
        },
        sortedAndFilteredActive() {
            return this.sortedAndFiltered.filter(x=>!x.isArchived);
        },
        sortedAndFilteredArchived() {
            return this.sortedAndFiltered.filter(x=>x.isArchived);
        },
        sortedAndFiltered() {
            return this.data.filter(this.sandpitFilter).sort((a, b) => a.displayOrder - b.displayOrder);
        }
    }
};
</script>
<style lang="scss" scoped></style>
